/**
 * Shared data-viz theme.
 *
 * Every value here resolves to a CSS custom property declared in
 * assets/style/main.css, so the charts and the rest of the UI stay on one
 * palette. Recharts needs real colour strings for SVG fills, so we read the
 * tokens off :root once instead of hardcoding hex in the components.
 */

const FALLBACKS = {
  "--viz-series-1": "#2a78d6",
  "--viz-series-2": "#eb6834",
  "--viz-series-3": "#1baf7a",
  "--viz-series-4": "#eda100",
  "--viz-series-5": "#e87ba4",
  "--viz-neutral": "#c3c2b7",
  "--viz-ordinal-1": "#86b6ef",
  "--viz-ordinal-2": "#2a78d6",
  "--viz-ordinal-3": "#104281",
  "--viz-surface": "#ffffff",
  "--viz-ink": "#1F222A",
  "--viz-ink-muted": "#667085",
  "--viz-grid": "#e9edf3",
  "--viz-axis": "#c3c2b7",
};

const token = (name) => {
  if (typeof window === "undefined") return FALLBACKS[name];
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || FALLBACKS[name];
};

/**
 * Categorical hues, in fixed order. Slot N is always the same hue no matter
 * how many series are on screen - never cycle past the end of this list. A
 * ninth category folds into `NEUTRAL` ("Other") instead.
 */
export const seriesColor = (index) =>
  token(`--viz-series-${Math.min(index, 4) + 1}`);

/* Resolved at call time, not import time - the tokens only exist once
   main.css has been applied. */
export const series = (count = 5) =>
  Array.from({ length: count }, (_, i) => seriesColor(i));

/** Reserved for "Other" / "N/A" / "no answer". Never a data series. */
export const NEUTRAL = () => token("--viz-neutral");

/** Ordinal ramp - one hue, light to dark. Only for genuinely ordered bands. */
export const ORDINAL = () => [
  token("--viz-ordinal-1"),
  token("--viz-ordinal-2"),
  token("--viz-ordinal-3"),
];

export const chrome = () => ({
  surface: token("--viz-surface"),
  ink: token("--viz-ink"),
  muted: token("--viz-ink-muted"),
  grid: token("--viz-grid"),
  axis: token("--viz-axis"),
});

/** Bars are capped rather than filling their band - the leftover is air. */
export const MAX_BAR_SIZE = 24;

/** Rounded data-end, square at the baseline. */
export const barRadius = (layout) =>
  layout === "vertical" ? [0, 4, 4, 0] : [4, 4, 0, 0];

/** Shared axis styling - recessive, hairline, never dashed. */
export const axisProps = (theme) => ({
  tickLine: false,
  axisLine: { stroke: theme.axis },
  tick: { fill: theme.muted, fontSize: 11 },
});

export const labelProps = (theme) => ({
  fill: theme.ink,
  fontSize: 11,
  fontWeight: 700,
});

/** Values never carry the series colour - text stays on ink tokens. */
export const percentOf = (value, total) =>
  total ? `${Math.round((value / total) * 100)}%` : "0%";
